import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { get } from "@/lib/api";
import { PageHeader, Section } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { money, relative } from "@/lib/format";
import { MiniCalendarWidget } from "@/components/dashboard/MiniCalendarWidget";
import {
  Wallet, Target, Trophy, Building2, Coins, CalendarClock, FileText,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const CHART = ["#2563EB", "#22C55E", "#F59E0B", "#8B5CF6", "#0EA5E9", "#EC4899"];

export default function CrmDashboard() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ["crm-dashboard"], queryFn: () => get("/crm/dashboard") });

  if (isLoading || !data) {
    return (
      <div className="grid grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
    );
  }
  const k = data.kpis;

  return (
    <div>
      <PageHeader title="CRM Overview" subtitle="Pipeline health, activity, and your schedule at a glance." />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <KpiCard testid="crm-kpi-pipeline" variant="hero" accent="blue" label="Pipeline Value" value={money(k.pipeline_value)}
          sub={`${k.open_leads} open leads`} icon={Wallet} spark={data.pipeline_trend} className="col-span-2" />
        <KpiCard testid="crm-kpi-won" variant="hero" accent="green" label="Won Value" value={money(k.won_value)}
          sub={`${k.won_count} deals won`} icon={Trophy} spark={data.won_trend} className="col-span-2" />
        <KpiCard testid="crm-kpi-leads" accent="amber" label="Open Leads" value={k.open_leads} sub="in pipeline" icon={Target} />
        <KpiCard testid="crm-kpi-clients" accent="violet" label="Active Clients" value={k.active_clients} sub="accounts" icon={Building2} />
        <KpiCard testid="crm-kpi-avg" accent="primary" label="Avg Deal Size" value={money(k.avg_deal_size)} sub="per won deal" icon={Coins} />
        <KpiCard testid="crm-kpi-renewals" accent="red" label="Renewals Due" value={k.upcoming_renewals} sub="within 60 days" icon={CalendarClock} />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Section title="Sales Funnel" testid="crm-dashboard-funnel">
            <div className="p-4" style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data.funnel}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="stage" tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 12, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[6, 6, 0, 0]} maxBarSize={56} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Section>

          <Section title="Leads by Source" testid="crm-dashboard-source">
            <div className="p-4" style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data.by_source} dataKey="count" nameKey="source" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                    {data.by_source.map((_, i) => <Cell key={i} fill={CHART[i % CHART.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="mt-2 flex flex-wrap gap-2">
                {data.by_source.map((s, i) => (
                  <span key={s.source} className="flex items-center gap-1.5 text-xs capitalize text-muted-foreground">
                    <span className="h-2 w-2 rounded-full" style={{ background: CHART[i % CHART.length] }} />{s.source}
                  </span>
                ))}
              </div>
            </div>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Calendar" testid="crm-dashboard-calendar" action={
            <button onClick={() => navigate("/calendar")} className="text-xs text-primary hover:underline">Full calendar</button>
          }>
            <div className="p-3">
              <MiniCalendarWidget />
            </div>
          </Section>

          <Section title="Recent Activity" testid="crm-dashboard-activity">
            <div className="divide-y divide-border">
              {data.recent_activity.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">No recent activity.</div>
              ) : data.recent_activity.slice(0, 6).map((a) => (
                <div key={a.id} className="flex items-start gap-2.5 px-4 py-2.5">
                  <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="text-[13px] leading-snug text-foreground">
                    <span className="font-medium">{a.author_name}</span> logged a <StatusBadge family="slate" label={a.type} /> on <span className="font-medium">{a.company_name}</span>
                    <span className="block text-[11px] text-muted-foreground">{relative(a.created_at)}</span>
                  </span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
